import styled from 'styled-components';

import { ASIDE_WIDTH } from '@/core/components/Layout/Layout.styles';

import { getVariant } from '@/themes/typographyUtilis';

import { tableCellStyles } from '../ChatContent/MarkdownText.styles';

const CONTENT_MAX_WIDTH = 850;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100vw - ${ASIDE_WIDTH});
  height: 100%;
  min-height: 100%;
  background-color: ${({ theme }) => theme.colors.background.medium};
`;

export const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${CONTENT_MAX_WIDTH}px;
  height: 100%;
  min-height: 0;
  margin: 0 auto;
`;

export const ViewHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 16px 24px;
`;

export const TitleWrapper = styled.div`
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;

export const HeaderActions = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

export const ViewBody = styled.div.attrs({ className: 'scrollbar-hidden' })`
  overflow: auto;
  flex: 1;
  padding: 24px;
`;

export const ContentCard = styled.div`
  padding: 24px 0 0;
`;

export const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
`;

export const LocationLabel = styled.span`
  color: ${({ theme }) => theme.colors.teal};
  font-weight: 600;
  font-size: 14px;
`;

export const DescriptionSection = styled.div`
  margin-bottom: 24px;
`;

/** HTML from Quill – alignment from editor (ql-align-*) preserved, typography/colors from theme */
export const DescriptionBlockHtml = styled.div`
  color: ${({ theme }) => theme.colors.primaryText};
  ${getVariant({ variant: 'body1-normal' })}

  & p {
    margin: 0 0 12px;
    text-align: left; /* default – ql-align-* classes override per block */
  }

  & p:last-child {
    margin-bottom: 0;
  }

  /* Collapse empty blocks – Quill often inserts these when toggling alignment */
  & p:empty,
  & p:has(> br:only-child) {
    overflow: hidden;
    height: 0;
    min-height: 0;
    margin: 0;
    padding: 0;
  }

  & div.ql-align-left:empty,
  & div.ql-align-center:empty,
  & div.ql-align-right:empty,
  & div.ql-align-justify:empty,
  & div.ql-align-left:has(> br:only-child),
  & div.ql-align-center:has(> br:only-child),
  & div.ql-align-right:has(> br:only-child),
  & div.ql-align-justify:has(> br:only-child) {
    overflow: hidden;
    height: 0;
    min-height: 0;
    margin: 0;
    padding: 0;
  }

  & h2 {
    margin: 0 0 12px;
    color: ${({ theme }) => theme.colors.primaryText};
    ${getVariant({ variant: 'h2' })}
  }

  & h3 {
    margin: 0 0 10px;
    color: ${({ theme }) => theme.colors.primaryText};
    ${getVariant({ variant: 'h3' })}
  }

  & ul,
  & ol {
    margin: 0 0 12px;
    padding-left: 18px;
    color: ${({ theme }) => theme.colors.primaryText};
  }

  & li {
    margin-bottom: 6px;
    color: ${({ theme }) => theme.colors.primaryText};
  }

  & li::marker {
    color: ${({ theme }) => theme.colors.primaryText};
  }

  & strong {
    font-weight: 700;
  }

  & a {
    color: ${({ theme }) => theme.colors.teal};
    text-decoration: underline;
  }

  & a:hover {
    opacity: 0.9;
  }

  & table {
    width: 100%;
    margin: 0 0 12px;
    border-collapse: collapse;
    color: ${({ theme }) => theme.colors.primaryText};
  }

  ${tableCellStyles}

  /* Quill alignment classes – editor justify/left/center/right shows in view */
  .ql-align-left {
    text-align: left;
  }

  .ql-align-center {
    text-align: center;
  }

  .ql-align-right {
    text-align: right;
  }

  .ql-align-justify {
    text-align: justify;
  }
`;

export const DescriptionBlock = styled.div<{ $justify?: boolean }>`
  text-align: ${({ $justify = true }) => ($justify ? 'justify' : 'left')};

  & p {
    margin: 0 0 12px;
  }

  & p:last-child {
    margin-bottom: 0;
  }

  & a {
    color: ${({ theme }) => theme.colors.teal};
    text-decoration: underline;
  }

  & a:hover {
    opacity: 0.9;
  }
`;

export const JustifyToggleRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

export const PublicationDate = styled.span`
  display: block;
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.secondaryText};
  ${getVariant({ variant: 'body2-normal' })}
`;

export const CloseButton = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: none;
  background: transparent;
  cursor: pointer;
`;

export const MetadataSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: 24px;
`;

export const MetadataField = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const MetadataFieldLabel = styled.span`
  flex-shrink: 0;
  min-width: 90px;
`;

export const MetadataValuePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const MetadataPill = styled.span<{
  $backgroundColor?: string;
  $textColor?: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border-radius: 4px;
  padding: 4px 10px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ theme, $textColor }) => $textColor ?? theme.colors.primaryText};
  font-size: 13px;
  line-height: 1.3;
`;

// TODO: Fix icons size
export const MetadataPillIconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  width: 1em;
  max-width: 1em;
  height: 1em;
  max-height: 1em;
  margin-left: -2px;

  & img {
    width: auto;
    max-width: 100%;
    height: auto;
    max-height: 100%;
    vertical-align: middle;
    object-fit: contain;
  }
`;
